import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MarketLayout from '../components/MarketLayout';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const TABS = [
  { key: 'all',         label: 'All' },
  { key: 'submitted',   label: 'Submitted' },
  { key: 'loi_pending', label: 'LOI Pending' },
  { key: 'loi_signed',  label: 'LOI Signed' },
  { key: 'dd_open',     label: 'DD Open' },
  { key: 'rejected',    label: 'Rejected' },
];

const STATUS_STYLE = {
  submitted:    { bg: '#f3f4f6', color: '#374151', label: 'Submitted' },
  loi_pending:  { bg: '#fffde4', color: '#111111', label: 'LOI Pending' },
  loi_signed:   { bg: '#eff4ff', color: '#3a6ad9', label: 'LOI Signed' },
  dd_open:      { bg: '#f0fde4', color: '#111111', label: 'DD Open' },
  rejected:     { bg: '#ffe6e9', color: '#ff082f', label: 'Rejected' },
  withdrawn:    { bg: '#f3f4f6', color: '#6b7280', label: 'Withdrawn' },
};

function fmt$(n) {
  const v = Number(n) || 0;
  return v ? `$${Math.round(v).toLocaleString()}` : '—';
}
function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || { bg: '#f3f4f6', color: '#374151', label: status || '—' };
  return (
    <span style={{ display: 'inline-block', padding: '3px 10px', borderRadius: 9999, backgroundColor: s.bg, color: s.color, fontSize: 11, fontWeight: 700 }}>
      {s.label}
    </span>
  );
}

export default function MarketBids() {
  const navigate = useNavigate();
  const token = localStorage.getItem('doorly_admin_token');
  const [bids, setBids] = useState([]);
  const [tab, setTab] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState(null);
  const [reviewingBid, setReviewingBid] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/admin/market/bids`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBids(res.data.bids || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load bids.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(
    () => (tab === 'all' ? bids : bids.filter((b) => b.status === tab)),
    [bids, tab]
  );

  const handleAccept = async (b) => {
    if (!window.confirm(`Accept ${b.investor_first_name}'s bid (${b.bid_price_pct}% on ${b.pool_number}) and reject all other bids on this pool?`)) return;
    setBusyId(b.id);
    try {
      await axios.patch(
        `${API_URL}/api/admin/market/bids/${b.id}/accept`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed.');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async () => {
    if (!reviewingBid) return;
    try {
      await axios.patch(
        `${API_URL}/api/admin/market/bids/${reviewingBid.id}/reject`,
        { rejectionReason: rejectReason || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReviewingBid(null);
      setRejectReason('');
      await load();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed.');
    }
  };

  return (
    <MarketLayout>
      <div style={{ padding: 24 }}>
        <div style={{ marginBottom: 18 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#111827', margin: 0 }}>Bids</h1>
          <p style={{ fontSize: 13, color: '#6b7280', margin: '4px 0 0' }}>
            Investor bids across every DoorlyMarket pool.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 14, borderBottom: '1px solid #e5e7eb', flexWrap: 'wrap' }}>
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: '10px 16px',
                fontSize: 13,
                fontWeight: 600,
                color: tab === t.key ? '#3a6ad9' : '#6b7280',
                backgroundColor: 'transparent',
                border: 'none',
                borderBottom: tab === t.key ? '2px solid #3a6ad9' : '2px solid transparent',
                cursor: 'pointer',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ backgroundColor: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>Loading…</div>
          ) : error ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#ff082f', fontSize: 14 }}>{error}</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: '#9ca3af', fontSize: 14 }}>No bids in this view.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <Th>Pool</Th>
                  <Th>Investor</Th>
                  <Th>Firm</Th>
                  <Th align="right">Bid %</Th>
                  <Th align="right">Bid Amount</Th>
                  <Th>Status</Th>
                  <Th>Submitted</Th>
                  <Th align="right">Actions</Th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((b) => (
                  <tr key={b.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <Td>
                      <div style={{ fontWeight: 600, color: '#3a6ad9', cursor: 'pointer' }} onClick={() => navigate(`/admin/market/pools/${b.pool_id}?tab=bids`)}>
                        {b.pool_name}
                      </div>
                      <div style={{ color: '#6b7280', fontSize: 12 }}>{b.pool_number}</div>
                    </Td>
                    <Td>
                      <div style={{ fontWeight: 600 }}>{b.investor_first_name} {b.investor_last_name}</div>
                      <div style={{ color: '#6b7280', fontSize: 12 }}>{b.investor_email}</div>
                    </Td>
                    <Td>{b.investor_firm_name || '—'}</Td>
                    <Td align="right">{Number(b.bid_price_pct).toFixed(2)}%</Td>
                    <Td align="right">{fmt$(b.bid_amount)}</Td>
                    <Td><StatusPill status={b.status} /></Td>
                    <Td>{fmtDate(b.submitted_at)}</Td>
                    <Td align="right">
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                        <button onClick={() => navigate(`/admin/market/pools/${b.pool_id}?tab=bids`)} style={btnGhost}>View</button>
                        {b.status === 'submitted' && (
                          <>
                            <button onClick={() => handleAccept(b)} disabled={busyId === b.id} style={btnGreen}>Accept</button>
                            <button onClick={() => { setReviewingBid(b); setRejectReason(''); }} style={btnRed}>Reject</button>
                          </>
                        )}
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {reviewingBid && (
        <div style={modalBackdrop}>
          <div style={modalBox}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#111827', marginBottom: 8 }}>
              Reject bid from {reviewingBid.investor_first_name} {reviewingBid.investor_last_name}?
            </div>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
              Investor will receive an email. Reason is optional.
            </p>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Optional reason"
              style={{ width: '100%', minHeight: 80, padding: 10, borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', marginBottom: 14, boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => { setReviewingBid(null); setRejectReason(''); }} style={btnGhost}>Cancel</button>
              <button onClick={handleReject} style={btnRed}>Confirm Reject</button>
            </div>
          </div>
        </div>
      )}
    </MarketLayout>
  );
}

const Th = ({ children, align = 'left' }) => (
  <th style={{ textAlign: align, padding: '10px 16px', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5 }}>{children}</th>
);
const Td = ({ children, align = 'left' }) => (
  <td style={{ padding: '12px 16px', textAlign: align, color: '#111827', verticalAlign: 'middle' }}>{children}</td>
);

const btnBase    = { fontSize: 12, fontWeight: 700, padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer' };
const btnGreen   = { ...btnBase, backgroundColor: '#acf44e', color: '#fff' };
const btnRed     = { ...btnBase, backgroundColor: '#ff082f', color: '#fff' };
const btnGhost   = { ...btnBase, backgroundColor: 'transparent', color: '#374151', border: '1px solid #e5e7eb' };

const modalBackdrop = { position: 'fixed', inset: 0, backgroundColor: 'rgba(15, 23, 42, 0.6)', zIndex: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
const modalBox = { backgroundColor: '#fff', borderRadius: 14, padding: 24, maxWidth: 480, width: '100%' };
